import { useNavigate } from "react-router-dom";
import { Button, Container, Divider, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";

import { IssuerItemList } from "../IssuerItem/IssuerItemList";
import { IssuerDetailData } from "../../type";
import main from "../../assets/scss/main.module.scss";
import { Balance } from "../common/Balance";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

export const Info = ({
  issuerDetailData,
}: {
  issuerDetailData: IssuerDetailData;
}) => {
  const navigate = useNavigate();

  const editDateFormat = (dateStr?: string): string => {
    if (dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString("ja-JP");
    } else {
      return "";
    }
  };

  const textToLink = (comment: string) => {
    const regexpUrl = /(https?:\/\/[\w/:%#$&?()~.=+-]+)/g;
    let linkedComment = comment.replace(
      regexpUrl,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    return linkedComment;
  };

  return (
    <>
      {issuerDetailData?.stampCardList &&
        issuerDetailData.stampCardList.length > 0 && (
          <>
            <Container
              sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  mx: "auto",
                  my: 2,
                  py: 2,
                  width: "280px",
                  height: "80px",
                  justifyContent: "left",
                }}
                onClick={() => {
                  navigate(
                    `/rally/${issuerDetailData?.stampCardList?.[0].uuid}`,
                    {
                      state: { issuerId: issuerDetailData.uuid },
                    }
                  );
                }}
              >
                <ConfirmationNumberIcon
                  fontSize="large"
                  sx={{ color: main.colorDarkGreen }}
                />
                <Typography sx={{ fontWeight: "bold", ml: 1.5 }}>
                  スタンプカードを開く
                </Typography>
              </Button>
            </Container>
          </>
        )}
      <Typography variant="h6" fontWeight="bold">
        基本情報
      </Typography>
      <Container
        sx={{
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          my: 2,
        }}
      >
        {issuerDetailData.address && (
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <PlaceIcon sx={{ color: main.colorDarkGreen }} />
            {issuerDetailData.address}
          </Container>
        )}
        {issuerDetailData.tel && (
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <PhoneIcon sx={{ color: main.colorDarkGreen }} />
            {issuerDetailData.tel}
          </Container>
        )}
        {issuerDetailData.email && (
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <EmailIcon sx={{ color: main.colorDarkGreen }} />
            {issuerDetailData.email}
          </Container>
        )}
        {issuerDetailData.web && (
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LanguageIcon sx={{ color: main.colorDarkGreen }} />
            {issuerDetailData.web}
          </Container>
        )}
        {issuerDetailData.businessHour && (
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AccessTimeIcon sx={{ color: main.colorDarkGreen }} />
            <Typography
              sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            >
              {issuerDetailData.businessHour}
            </Typography>
          </Container>
        )}
        {(issuerDetailData.eventStartDate || issuerDetailData.eventEndDate) && (
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CalendarMonthIcon sx={{ color: main.colorDarkGreen }} />
            {issuerDetailData.eventStartDate ===
              issuerDetailData.eventEndDate && (
              <>{editDateFormat(issuerDetailData.eventStartDate)}</>
            )}
            {issuerDetailData.eventStartDate !==
              issuerDetailData.eventEndDate && (
              <>
                {editDateFormat(issuerDetailData.eventStartDate)} ~{" "}
                {editDateFormat(issuerDetailData.eventEndDate)}
              </>
            )}
          </Container>
        )}
      </Container>
      {issuerDetailData.description && (
        <>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }}>
            詳細情報
          </Typography>
          <Container
            sx={{
              whiteSpace: "break-spaces",
              px: 2,
              overflowWrap: "anywhere",
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: textToLink(issuerDetailData.description),
              }}
            ></div>
          </Container>
        </>
      )}

      {issuerDetailData.couponList &&
        issuerDetailData.couponList.length > 0 && (
          <>
            <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }}>
              クーポン情報
            </Typography>
            <IssuerItemList
              itemList={issuerDetailData.couponList}
              type="coupon"
            />
          </>
        )}

      {issuerDetailData.flyerList && issuerDetailData.flyerList.length > 0 && (
        <>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }}>
            おすすめ・お得情報
          </Typography>
          <IssuerItemList itemList={issuerDetailData.flyerList} type="flyer" />
        </>
      )}

      {issuerDetailData.rallyList && issuerDetailData.rallyList.length > 0 && (
        <>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }}>
            参加しているスタンプラリー
          </Typography>
          <IssuerItemList
            itemList={issuerDetailData.rallyList}
            type="rally"
            issuerId={issuerDetailData.uuid}
          />
        </>
      )}

      {issuerDetailData.type === "event" && (
        <>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }}>
            出店者一覧
          </Typography>
          {issuerDetailData.eventStoreList.map((name: string, index) => (
            <Container key={index} sx={{ my: 1 }}>
              {name}
            </Container>
          ))}
        </>
      )}
    </>
  );
};
